import {put, call, all} from 'redux-saga/effects';
import TemplateTask from "../../entities/templateTask";
import * as Actions from "../actions/templateTaskActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type TemplateTaskAction = {
    templateTaskId: string;
    templateTask: TemplateTask;
    templateTasks: TemplateTask[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneTemplateTask(action: TemplateTaskAction) {
	const session = tabSession.captureGeneration();
	try {
		const templateTask: TemplateTask = yield call(api.findOne, 'templateTask', action.templateTaskId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.TEMPLATETASK_FETCH_ONE_SUCCESS, templateTaskId: action.templateTaskId, templateTask, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.TEMPLATETASK_FETCH_ONE_FAILURE, templateTaskId: action.templateTaskId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {TemplateTaskAction} action
 */
export function* saveTemplateTask(action: TemplateTaskAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ TEMPLATETASK_SAVE saga', action.templateTask?.id);

		if (action.templateTask === undefined || action.templateTask === null) {
			console.error('saveTemplateTask received a null');
			throw new Error('saveTemplateTask received a null');
		}

		if (!(action.templateTask instanceof TemplateTask)) {
			action.templateTask = new TemplateTask(action.templateTask);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.templateTask.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.templateTask);
			const addResult = yield call(idb.add, 'templateTask', action.templateTask);
			if (addResult.isErr()) {
				console.error(`⮂ saveTemplateTask: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertTemplateTaskSave(action.templateTask, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.TEMPLATETASK_SAVE_FAILURE, templateTask: action.templateTask, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.TEMPLATETASK_SAVE, templateTask: action.templateTask, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertTemplateTaskSave(action.templateTask, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.TEMPLATETASK_SAVE_FAILURE, templateTask: action.templateTask, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {TemplateTaskAction} action
 * @param {TemplateTask[]} action.templateTasks
 */
export function* saveManyTemplateTasks(action: TemplateTaskAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ TEMPLATETASK_SAVE_MANY saga', action.templateTasks.map(templateTask => templateTask.id).join(','));

        if (!Array.isArray(action.templateTasks)) {
            console.error('saveManyTemplateTasks expected an array, received', action.templateTasks);
            throw new Error('saveManyTemplateTasks expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveTemplateTask), under these queue keys
		const dispatchedAddedOns: number[] = action.templateTasks.map(templateTask => templateTask?.addedOn);
		const revertAll = () => action.templateTasks.map((templateTask, i) =>
			put(Actions.revertTemplateTaskSave(templateTask, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: TemplateTask[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.templateTasks.length; i++) {
				const templateTask = action.templateTasks[i];
				templateTask.persisted = false;
				ensureOperationId(templateTask);
				toSave.push(templateTask);
			}

			const addResult = yield call(idb.add, 'templateTask', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyTemplateTasks: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.TEMPLATETASK_SAVE_MANY_FAILURE, templateTasks: action.templateTasks, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.TEMPLATETASK_SAVE_MANY, templateTasks: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.TEMPLATETASK_SAVE_MANY_FAILURE, templateTasks: action.templateTasks, status: 500, err});

			console.error(`Error saving ${action.templateTasks.length} templateTasks to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {TemplateTaskAction} action
 */
export function* removeTemplateTask(action: TemplateTaskAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.templateTask === undefined || action.templateTask === null || !(action.templateTask instanceof TemplateTask)) {
			console.error('removeTemplateTask expected type TemplateTask, received', action.templateTask);
			reportQueued(false);
			throw new Error('removeTemplateTask expected type TemplateTask');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.templateTask.status = 'D';
		action.templateTask.addedOn = newHRTimestamp();
		action.templateTask.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.templateTask);
		const addResult = yield call(idb.add, 'templateTask', action.templateTask);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeTemplateTask: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.TEMPLATETASK_REMOVE_FAILURE, templateTask: action.templateTask, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.TEMPLATETASK_REMOVE, templateTask: action.templateTask});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.TEMPLATETASK_REMOVE_FAILURE, templateTask: action.templateTask, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this TemplateTask.
	*
	* @param {TemplateTaskAction} action
	*/
export function* restoreTemplateTask(action: TemplateTaskAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreTemplateTask expected getCurrentUserId() to return a value');
        return;
    }

	if (action.templateTask === undefined || action.templateTask === null) {
        console.error('restoreTemplateTask expected a TemplateTask, received null');
        throw new Error('restoreTemplateTask expected a TemplateTask, received null');
    }
    else if (!(action.templateTask instanceof TemplateTask)) action.templateTask = new TemplateTask(action.templateTask); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.templateTask.status = 'U';
		action.templateTask.addedOn = newHRTimestamp();
		action.templateTask.addedBy = userId;
		action.templateTask.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.templateTask);
		const addResult = yield call(idb.add, 'templateTask', action.templateTask);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreTemplateTask: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.TEMPLATETASK_RESTORE_FAILURE, templateTask: action.templateTask, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.TEMPLATETASK_RESTORE, templateTask: action.templateTask});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.TEMPLATETASK_RESTORE_FAILURE, templateTask: action.templateTask, status: 500, err});

		console.error('Error restoring from database', err);
	}
}